import { createContext, createElement, useContext, type ReactElement, type ReactNode } from 'react';
import type { ChatAPIProxyClient } from '../clients/index.js';

export type CustomPageAction = {
  type: `custom-page/${string}`;
  payload?: object;
};
export type CustomPageDispatchAction = (action: CustomPageAction) => void;

export type CustomPageSubject = {
  id: number | string;
  name: string;
  kind?: string;
} | null;

export type CustomPageRendererContextValue = {
  proxiesClient: ChatAPIProxyClient | null;
  subject: CustomPageSubject;
  dispatchAction: CustomPageDispatchAction;
};

export const CustomPageProxiesClientContext = createContext<ChatAPIProxyClient | null>(null);
export const CustomPageSubjectContext = createContext<CustomPageSubject>(null);
const noopCustomPageDispatchAction: CustomPageDispatchAction = () => {};
export const CustomPageDispatchActionContext = createContext<CustomPageDispatchAction>(
  noopCustomPageDispatchAction
);

type CustomPageRendererContextProviderProps = {
  value: CustomPageRendererContextValue;
  children: ReactNode;
};

export function CustomPageRendererContextProvider({
  value,
  children,
}: CustomPageRendererContextProviderProps): ReactElement {
  return createElement(
    CustomPageDispatchActionContext.Provider,
    { value: value.dispatchAction },
    createElement(
      CustomPageProxiesClientContext.Provider,
      { value: value.proxiesClient },
      createElement(CustomPageSubjectContext.Provider, { value: value.subject }, children)
    )
  );
}

export function useCustomPageProxiesClient(): ChatAPIProxyClient {
  const ctx = useContext(CustomPageProxiesClientContext);
  if (ctx === null) {
    throw new Error('useCustomPageProxiesClient must be used within a CustomPageRendererContextProvider');
  }
  return ctx;
}

export function useCustomPageSubject(): CustomPageSubject {
  return useContext(CustomPageSubjectContext);
}

export function useCustomPageDispatchAction(): CustomPageDispatchAction {
  return useContext(CustomPageDispatchActionContext);
}
